import isElectron from 'is-electron';

import i18n from '/@/i18n/i18n';
import { AuthSessionRef, isAuthSessionCurrent } from '/@/renderer/api/auth-session';
import { useAuthStore } from '/@/renderer/store';
import { logger } from '/@/renderer/utils/logger';
import { toast } from '/@/shared/components/toast/toast';

const localSettings = isElectron() ? window.api.localSettings : null;

const AUTH_FAILURE_TOAST_ID = 'auth-failure';

/** The stored entry `ref` has to match: the selected server if it is that one, else the list entry. */
const storedServerFor = (serverId: string) => {
    const store = useAuthStore.getState();
    return store.currentServer?.id === serverId
        ? store.currentServer
        : (store.serverList[serverId] ?? null);
};

/** Is the sign-in a request was sent with still the stored one for its server? */
export const isRequestSessionCurrent = (ref: AuthSessionRef | null | undefined): boolean =>
    Boolean(ref) && isAuthSessionCurrent(ref, storedServerFor(ref!.serverId));

/**
 * The server rejected the sign-in `ref`. Ends it (toast, forget the saved
 * password, logout) only while it is still the stored sign-in of its server:
 * a rejection of an earlier login, or of a request sent without credentials,
 * must not end the session the user has now.
 */
export const authenticationFailure = (
    ref: AuthSessionRef | null | undefined,
    message?: string,
): boolean => {
    if (!ref || !isRequestSessionCurrent(ref)) {
        logger.debug('Ignored authentication failure of a previous sign-in', {
            serverId: ref?.serverId,
        });
        return false;
    }

    const store = useAuthStore.getState();
    const serverId = ref.serverId;

    toast.error({
        id: AUTH_FAILURE_TOAST_ID,
        message: message ?? (i18n.t('error.sessionExpiredError') as string),
    });

    // Never log the credential itself: it is a live secret of the current sign-in.
    logger.warn('Session expired, signing out', { serverId });
    localSettings?.passwordRemove(serverId);

    // logout() clears credentials on the server list entry and sets currentServer to null.
    // A rejected sign-in of another (not selected) server only clears that entry.
    if (store.currentServer?.id === serverId) {
        store.actions.logout();
    } else {
        store.actions.updateServer(serverId, {
            credential: '',
            ndCredential: undefined,
            savePassword: false,
        });
    }

    return true;
};
